import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "../../lib/api";
import CustomerHeader from "../../components/CustomerHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import { CalendarDays, Users, Clock, MapPin } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);
const STATE_LABELS = {
  richiesta: "Richiesta inviata", in_attesa: "In attesa", confermata: "Confermata",
  in_corso: "In corso", completata: "Completata", annullata_cliente: "Annullata",
  annullata_attivita: "Annullata dall'attività", no_show: "No-show", rimborsata: "Rimborsata",
};

export default function Reservations() {
  const [mine, setMine] = useState([]);
  const [biz, setBiz] = useState([]);
  const [bid, setBid] = useState("");
  const [date, setDate] = useState(today());
  const [party, setParty] = useState(2);
  const [slots, setSlots] = useState(null);
  const [time, setTime] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [params] = useSearchParams();

  const loadMine = () => api.get("/booking/mine").then(({ data }) => setMine(data)).catch(() => {});
  useEffect(() => {
    loadMine();
    api.get("/booking/enabled-businesses").then(({ data }) => setBiz(data)).catch(() => {});
  }, []);
  useEffect(() => {
    const bs = params.get("booking_session");
    if (bs) api.get(`/payments/status/${bs}`).then(({ data }) => { if (data.payment_status === "paid") { toast.success("Caparra pagata: prenotazione confermata!"); loadMine(); } }).catch(() => {});
  }, [params]);

  const checkSlots = async () => {
    if (!bid) { toast.error("Scegli un'attività"); return; }
    setSlots(null); setTime("");
    try { const { data } = await api.get(`/booking/availability?business_id=${bid}&date=${date}&party_size=${party}`); setSlots(data.slots || []); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const reserve = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/booking/reserve", { business_id: bid, date, time, party_size: party, notes, origin_url: window.location.origin });
      if (data.checkout_url) { window.location.href = data.checkout_url; return; }
      toast.success("Prenotazione inviata!"); setSlots(null); setTime(""); loadMine();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  const cancel = async (id) => {
    try { const { data } = await api.put(`/booking/mine/${id}/cancel`); if (data?.penalty > 0) toast.warning(`Annullata. Penale prevista: €${data.penalty}`); else toast.success("Prenotazione annullata"); loadMine(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const suggest = async () => {
    try {
      const { data } = await api.post("/booking/ai/suggest-time", { business_id: bid, date, party_size: party });
      if (data.suggestions?.length) { setTime(data.suggestions[0].time); toast.success(`✨ Orari consigliati: ${data.suggestions.map((s) => s.time).join(", ")}`); }
      else toast.message(data.message || "Nessun suggerimento disponibile");
    } catch (e) { toast.error("Suggerimento non disponibile"); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0C] grain">
      <CustomerHeader />
      <div className="max-w-3xl mx-auto px-4 py-8 space-y-6" data-testid="reservations-page">
        <div>
          <h1 className="text-2xl font-display font-black">Le mie prenotazioni</h1>
          <p className="text-sm text-zinc-500">Tavoli, appuntamenti, campi e servizi in un unico posto.</p>
        </div>

        <div className="rounded-2xl p-5 border border-[#00E676]/25 bg-gradient-to-br from-[#00E676]/10 to-[#131316]" data-testid="booking-form">
          <div className="text-xs uppercase tracking-widest text-[#00E676] mb-3 flex items-center gap-2"><CalendarDays className="h-4 w-4" /> Nuova prenotazione</div>
          <div className="grid sm:grid-cols-3 gap-3">
            <div className="sm:col-span-3"><Label className="text-xs text-zinc-400">Attività</Label>
              <select value={bid} onChange={(e) => { setBid(e.target.value); setSlots(null); }} className="w-full mt-1 bg-[#0A0A0C] border border-white/10 rounded-md px-3 py-2 text-sm" data-testid="booking-business-select">
                <option value="">Scegli un'attività…</option>
                {biz.map((b) => <option key={b.id} value={b.id}>{b.name}{b.zone ? ` · ${b.zone}` : ""}</option>)}
              </select>
            </div>
            <div><Label className="text-xs text-zinc-400">Data</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="bg-[#0A0A0C] border-white/10 mt-1" data-testid="booking-date-input" /></div>
            <div><Label className="text-xs text-zinc-400">Persone</Label><Input type="number" min="1" value={party} onChange={(e) => setParty(Number(e.target.value))} className="bg-[#0A0A0C] border-white/10 mt-1" data-testid="booking-party" /></div>
            <div className="flex items-end"><Button onClick={checkSlots} className="w-full bg-[#7DF9FF] text-black" data-testid="booking-check">Vedi disponibilità</Button></div>
          </div>
          {slots !== null && (
            <div className="mt-4">
              {slots.length === 0 ? (
                <div className="text-sm text-amber-300">Nessuno slot disponibile per questa data. Prova un altro giorno.</div>
              ) : (
                <>
                  <div className="flex flex-wrap gap-2" data-testid="booking-slots">
                    {slots.map((s) => (
                      <button key={s} onClick={() => setTime(s)} data-testid={`slot-${s}`} className={`px-3 py-1.5 rounded-lg text-sm border ${time === s ? "border-[#00E676] bg-[#00E676]/10 text-[#00E676]" : "border-white/10 text-zinc-300"}`}>{s}</button>
                    ))}
                  </div>
                  <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Note (allergie, occasione, seggiolone…)" className="bg-[#0A0A0C] border-white/10 mt-3" data-testid="booking-notes" />
                  <Button onClick={reserve} disabled={busy || !time} className="mt-3 bg-[#00E676] text-black w-full" data-testid="booking-submit">{busy ? "Attendere…" : `Prenota ${time || ""}`}</Button>
                </>
              )}
            </div>
          )}
        </div>

        <div className="space-y-2">
          {mine.length === 0 && <div className="text-zinc-500 text-sm py-8 text-center border border-white/10 rounded-xl">Non hai ancora prenotazioni.</div>}
          {mine.map((b) => (
            <div key={b.id} data-testid={`my-booking-${b.id}`} className="rounded-xl border border-white/10 bg-[#1C1C1F] p-4 flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="font-semibold">{b.business_name}</div>
                <div className="text-xs text-zinc-500 flex items-center gap-3 mt-1"><span className="flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" />{b.date}</span><span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{b.time}</span><span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" />{b.party_size}p</span>{b.resource_name && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{b.resource_name}</span>}</div>
                <div className="text-xs mt-1 text-[#00E676]">{STATE_LABELS[b.status] || b.status}{b.deposit_amount > 0 ? ` · caparra €${b.deposit_amount} (${b.deposit_status})` : ""}</div>
              </div>
              {["richiesta", "in_attesa", "confermata"].includes(b.status) && <Button size="sm" variant="destructive" onClick={() => cancel(b.id)} data-testid={`cancel-my-${b.id}`}>Annulla</Button>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
